import { useEffect } from "react";
import Cookies from "universal-cookie";
import { CONFERENCE_COOKIE, CONFERENCE_PLACEMENT_COOKIE, CONFERENCE_COOKIE_OPTIONS, conferenceAcceptsEntries, isConferenceEntryPath, normalizeConferencePlacement } from "../../../src/shared/conferenceCampaign";
import { getImportantTrackingParams, isAuthenticationPath } from "../../../src/lib/trackingParams";

const COOKIE_NAME = "url_params";
const COOKIE_OPTIONS = {
  maxAge: 60 * 60 * 24 * 365 * 4, // 4 years
  path: "/",
};

// sub_first and annual control short-lived flow variants. sub_first_annual
// records that both arrived together, so signup doesn't combine stale cookies.
const ANNUAL_SUB_FIRST_COOKIE_NAME = "sub_first_annual";
const FLOW_COOKIE_OPTIONS = {
  maxAge: 60 * 60 * 24 * 7, // 1 week
  path: "/",
};

const normalizeCookieObject = (value) => {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return { ...value };
  if (typeof value !== "string") return {};

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

// This captures all important url parameters and saves them in a cookie
// this ignores customer.io utm tags, and also updates important tracking params every time
// Exported for tests; the hook below runs it once per mount.
export const captureURLParams = () => {
  const cookies = new Cookies();

  const storedValue = cookies.get(COOKIE_NAME);
  const currentValue = normalizeCookieObject(storedValue);

  // Clean old cookies even on authentication/customer.io routes that skip capture.
  const cleanValue = getImportantTrackingParams(currentValue);
  delete cleanValue.sub_first;
  delete cleanValue.annual;
  if (storedValue && (typeof storedValue !== "object" || Array.isArray(storedValue) || JSON.stringify(cleanValue) !== JSON.stringify(currentValue))) {
    cookies.set(COOKIE_NAME, JSON.stringify(cleanValue), COOKIE_OPTIONS);
  }
  const authenticationPath = isAuthenticationPath(window.location.pathname);
  // Preserve existing flow cookies without capturing any authentication URL data.
  const urlParams = new URLSearchParams(authenticationPath ? "" : window.location.search);

  const urlParamsObject = {};

  for (const [key, value] of urlParams.entries()) {
    urlParamsObject[key] = value;
  }

  if (isConferenceEntryPath(window.location.pathname) && conferenceAcceptsEntries(urlParamsObject.conference)) {
    cookies.set(CONFERENCE_COOKIE, urlParamsObject.conference, CONFERENCE_COOKIE_OPTIONS);
    cookies.set(CONFERENCE_PLACEMENT_COOKIE, normalizeConferencePlacement(urlParamsObject.utm_content), CONFERENCE_COOKIE_OPTIONS);
    ["sub_first", "annual", "sub_first_annual"].forEach(key => cookies.remove(key, { path: "/" }));
    delete currentValue.sub_first;
    delete currentValue.annual;
    delete urlParamsObject.sub_first;
    delete urlParamsObject.annual;
    cookies.set(COOKIE_NAME, JSON.stringify(cleanValue), COOKIE_OPTIONS);
  }
  const hasSubFirstParam = !!urlParamsObject.sub_first;
  const hasAnnualParam = !!urlParamsObject.annual;

  // Store sub_first in its own short-lived cookie instead of url_params.
  if (hasSubFirstParam) {
    // Fresh ad click — set the new 1-week cookie
    cookies.set("sub_first", urlParamsObject.sub_first, FLOW_COOKIE_OPTIONS);
  } else if (!hasAnnualParam && currentValue.sub_first && !cookies.get("sub_first")) {
    // Migrate: old url_params has sub_first but no new cookie yet.
    // Transfer it to the 1-week cookie so users already in the ad flow
    // aren't lost. The 1-week TTL handles natural expiry.
    cookies.set("sub_first", currentValue.sub_first, FLOW_COOKIE_OPTIONS);
  }

  // annual keeps logged-out pricing in yearly mode. sub_first_annual is the
  // stricter signup marker for the annual subscription-first flow.
  if (hasAnnualParam) {
    cookies.set("annual", urlParamsObject.annual, FLOW_COOKIE_OPTIONS);
    if (urlParamsObject.sub_first === "true" && urlParamsObject.annual === "true") {
      cookies.set(ANNUAL_SUB_FIRST_COOKIE_NAME, "true", FLOW_COOKIE_OPTIONS);
    } else {
      cookies.remove(ANNUAL_SUB_FIRST_COOKIE_NAME, { path: "/" });
    }
    if (!hasSubFirstParam) {
      cookies.remove("sub_first", { path: "/" });
    }
  } else if (hasSubFirstParam) {
    // A fresh sub_first click without annual is the non-annual flow
    // variant — it wins over any stale annual state.
    cookies.remove("annual", { path: "/" });
    cookies.remove(ANNUAL_SUB_FIRST_COOKIE_NAME, { path: "/" });
  } else if (currentValue.sub_first && currentValue.annual) {
    if (!cookies.get("annual")) {
      cookies.set("annual", currentValue.annual, FLOW_COOKIE_OPTIONS);
    }
    if (currentValue.sub_first === "true" && currentValue.annual === "true" && cookies.get("annual") !== "false") {
      cookies.set(ANNUAL_SUB_FIRST_COOKIE_NAME, "true", FLOW_COOKIE_OPTIONS);
    }
  }

  if (authenticationPath || urlParamsObject.utm_source === "customer.io") return;

  const incoming = getImportantTrackingParams(urlParamsObject);
  delete incoming.sub_first;
  delete incoming.annual;
  if (Object.keys(incoming).length > 0) {
    cookies.set(COOKIE_NAME, JSON.stringify({ ...cleanValue, ...incoming }), COOKIE_OPTIONS);
  }

};

const useCaptureURLParams = () => {
  useEffect(() => {
    captureURLParams();
  }, []);

  return null;
};

export default useCaptureURLParams;
